/**
 * NotificationManager - Handles sound and visual notifications
 * Features:
 * - Sound playback for new messages
 * - Title flash for inactive tabs
 * - Badge counter management
 */

class NotificationManager {
    private audioReceived: HTMLAudioElement | null = null;
    private audioSent: HTMLAudioElement | null = null;
    private originalTitle: string = document.title;
    private titleFlashInterval: number | null = null;
    private isFlashing: boolean = false;

    // Settings
    private soundEnabled: boolean = true;
    private readonly SOUND_RECEIVED = '/sounds/message-received.mp3';
    private readonly SOUND_SENT = '/sounds/message-sent.mp3';
    private readonly SETTINGS_KEY = 'mavililer_notification_settings';

    constructor() {
        this.loadSettings();
        this.initializeAudio();

        // Restore title when tab becomes active
        document.addEventListener('visibilitychange', () => {
            if (!document.hidden) {
                this.stopTitleFlash();
            }
        });
    }

    // ===== INITIALIZATION =====

    private loadSettings(): void {
        try {
            const settings = localStorage.getItem(this.SETTINGS_KEY);
            if (settings) {
                const parsed = JSON.parse(settings);
                this.soundEnabled = parsed.soundEnabled ?? true;
            }
        } catch (error) {
            console.error('Failed to load notification settings:', error);
        }
    }

    private saveSettings(): void {
        try {
            localStorage.setItem(
                this.SETTINGS_KEY,
                JSON.stringify({ soundEnabled: this.soundEnabled })
            );
        } catch (error) {
            console.error('Failed to save notification settings:', error);
        }
    }

    private initializeAudio(): void {
        try {
            this.audioReceived = new Audio(this.SOUND_RECEIVED);
            this.audioReceived.volume = 0.5;
            this.audioReceived.load();

            this.audioSent = new Audio(this.SOUND_SENT);
            this.audioSent.volume = 0.4;
            this.audioSent.load();
        } catch (error) {
            console.error('Failed to initialize audio:', error);
        }
    }

    // ===== SOUND PLAYBACK =====

    /**
     * Play notification sound for received message
     */
    playReceivedSound(): void {
        if (!this.soundEnabled || !this.audioReceived) return;
        this.audioReceived.currentTime = 0;
        this.audioReceived.play().catch(() => { });
    }

    /**
     * Play sound for sent message
     */
    playSentSound(): void {
        if (!this.soundEnabled || !this.audioSent) return;
        this.audioSent.currentTime = 0;
        this.audioSent.play().catch(() => { });
    }

    /**
     * Play notification sound (legacy support)
     */
    playSound(): void {
        this.playReceivedSound();
    }

    /**
     * Toggle sound on/off
     */
    toggleSound(): boolean {
        this.soundEnabled = !this.soundEnabled;
        this.saveSettings();
        console.log(`🔔 Sound ${this.soundEnabled ? 'enabled' : 'disabled'}`);
        return this.soundEnabled;
    }

    /**
     * Check if sound is enabled
     */
    isSoundEnabled(): boolean {
        return this.soundEnabled;
    }

    // ===== TITLE FLASH =====

    /**
     * Flash document title with message count
     * Only works when tab is inactive
     */
    flashTitle(count: number, message: string = 'Yeni Mesaj'): void {
        if (!document.hidden || this.isFlashing) {
            return;
        }

        this.isFlashing = true;
        let showNotification = true;

        const notificationTitle = `(${count}) ${message} - Mavililer`;

        this.titleFlashInterval = window.setInterval(() => {
            document.title = showNotification ? notificationTitle : this.originalTitle;
            showNotification = !showNotification;
        }, 1000); // Flash every second
    }

    /**
     * Stop title flashing and restore original title
     */
    stopTitleFlash(): void {
        if (this.titleFlashInterval !== null) {
            clearInterval(this.titleFlashInterval);
            this.titleFlashInterval = null;
        }
        this.isFlashing = false;
        document.title = this.originalTitle;
    }

    /**
     * Update the "base" title (for when app changes page)
     */
    updateBaseTitle(title: string): void {
        this.originalTitle = title;
        if (!this.isFlashing) {
            document.title = title;
        }
    }

    // ===== BROWSER NOTIFICATIONS (OPTIONAL) =====

    /**
     * Request browser notification permission
     * Returns true if granted, false otherwise
     */
    async requestPermission(): Promise<boolean> {
        if (!('Notification' in window)) {
            console.warn('Browser notifications not supported');
            return false;
        }

        if (Notification.permission === 'granted') {
            return true;
        }

        if (Notification.permission !== 'denied') {
            const permission = await Notification.requestPermission();
            return permission === 'granted';
        }

        return false;
    }

    /**
     * Show browser notification
     * Requires permission to be granted first
     */
    showBrowserNotification(
        title: string,
        options?: {
            body?: string;
            icon?: string;
            tag?: string;
        }
    ): void {
        if (!('Notification' in window) || Notification.permission !== 'granted') {
            return;
        }

        try {
            const notification = new Notification(title, {
                body: options?.body,
                icon: options?.icon || '/img/icon.png',
                tag: options?.tag || 'mavililer-message',
                requireInteraction: false,
            });

            // Auto-close after 5 seconds
            setTimeout(() => notification.close(), 5000);

            // Focus tab when notification is clicked
            notification.onclick = () => {
                window.focus();
                notification.close();
            };
        } catch (error) {
            console.error('Failed to show browser notification:', error);
        }
    }

    // ===== COMBINED NOTIFICATION =====

    /**
     * Trigger all notification types for a new message
     */
    notifyNewMessage(
        from: string,
        preview: string,
        unreadCount: number
    ): void {
        console.log('📬 Notification triggered:', { from, preview, unreadCount });

        // Play sound
        this.playSound();

        // Flash title if tab is inactive
        if (document.hidden) {
            this.flashTitle(unreadCount, 'Yeni Mesaj');
        }

        // Show browser notification (if permission granted)
        if (document.hidden || !document.hasFocus()) {
            this.showBrowserNotification(
                `${from}`,
                {
                    body: preview,
                    tag: 'dm-notification',
                }
            );
        }
    }

    // ===== CLEANUP =====

    /**
     * Clean up resources
     */
    destroy(): void {
        this.stopTitleFlash();
        if (this.audioReceived) {
            this.audioReceived.pause();
            this.audioReceived = null;
        }
        if (this.audioSent) {
            this.audioSent.pause();
            this.audioSent = null;
        }
    }
}

// Export singleton instance
export const notificationManager = new NotificationManager();
